import type { TColor } from '@Theme';
import type { FC } from 'react';
import React from 'react';
import type { StyleProp, TextStyle, ViewStyle } from 'react-native';
import { Platform } from 'react-native';
import styled, { css } from 'styled-components/native';

import OwnableTheme from '../../assets/OwnableTheme';
import { Text } from '../Common/Text';
import { Touchable } from './Touchable';
type IPrimaryButtonProps = {
  text: string;
  color?: TColor;
  pressable?: boolean;
  onPress?: () => void;
  rightMargin?: number;
  leftMargin?: number;
  topMargin?: number;
  textColor?: string;
  borderColor?: TColor;
  width?: number | string | null;
  padding?: number;
  styleOverride?: StyleProp<ViewStyle>;
  small?: boolean;
  tall?: boolean;
  minWidth?: number | string | null;
  fontStyleOverride?: StyleProp<TextStyle>;
  dropShadow?: boolean;
};

// noinspection CssUnknownProperty
const StyledButton = styled.View<{
  color: TColor;
  rightMargin: number;
  leftMargin?: number;
  topMargin: number;
  borderColor?: TColor;
  width?: number | string | null;
  padding?: number;
  small: boolean;
  tall?: boolean;
  minWidth?: number | string | null;
  dropShadow?: boolean;
}>`
  background-color: ${
    // @ts-expect-error
    ({ color }) => OwnableTheme.colors[color]
  };
  height: ${({ tall }) => (tall ? '60px' : '50px')};
  padding: ${({ padding }) => (padding ? `0px ${padding}px` : `0px 30px`)};
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${({ topMargin }) => `${topMargin}px`};
  margin-right: ${({ rightMargin }) => `${rightMargin}px`};
  margin-left: ${({ leftMargin }) => `${leftMargin}px`};
  border: ${({ borderColor }) =>
    borderColor
      ? `2px solid ${
          // @ts-expect-error
          OwnableTheme.colors[borderColor]
        }`
      : '2px solid lightgray'};
  border-width: ${({ borderColor }) => (borderColor ? '2px' : '0px')};
  ${({ minWidth }) =>
    minWidth &&
    css`
      min-width: 100px;
    `};
  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `};
  ${({ dropShadow }) =>
    dropShadow &&
    css`
      //noinspection CssUnknownProperty
      shadow-color: ${OwnableTheme.colors.persianIndigo};
      shadow-radius: 10px;
      shadow-opacity: 0.4;
      ${Platform.OS === 'android' &&
      css`
        //noinspection CssInvalidPropertyValue
        elevation: 2;
      `}
    `};
`;

export const PrimaryButton: FC<IPrimaryButtonProps> = ({
  text,
  color = 'paleBrown',
  pressable = true,
  onPress,
  rightMargin = 20,
  leftMargin = 0,
  topMargin = 0,
  textColor = 'persianIndigo',
  borderColor,
  width,
  padding,
  minWidth,
  styleOverride = {},
  fontStyleOverride = {},
  small = false,
  tall = false,
  dropShadow = false,
}) => (
  // @ts-expect-error
  <Touchable
    disabled={!pressable}
    onPress={onPress}
    containerStyle={styleOverride}
    style={styleOverride}
  >
    <StyledButton
      dropShadow={dropShadow}
      color={color}
      topMargin={topMargin}
      rightMargin={rightMargin}
      leftMargin={leftMargin}
      tall={tall}
      borderColor={borderColor}
      width={width}
      padding={padding}
      style={styleOverride}
      small={small}
      minWidth={minWidth}
    >
      <Text
        size={small ? 's' : 'm'}
        weight='bold'
        // @ts-expect-error - This works, it's just an index warning from TS
        color={OwnableTheme.colors?.[textColor] ?? textColor}
        fontStyleOverride={fontStyleOverride}
      >
        {text}
      </Text>
    </StyledButton>
  </Touchable>
);
